import { useState, useCallback } from 'react';
import { searchEntityTypesByText } from '~/services/autocomplete';
import { AutocompleteItem, AutocompleteSearchEntityType } from '@habity/types/autocomplete';
import debounce from 'lodash/debounce';

export const useAutocomplete = (entityTypes?: AutocompleteSearchEntityType[]) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<AutocompleteItem[]>([]);

  const loadOptions = useCallback(
    debounce(
      async (
        inputValue: string,
        callback: (options: AutocompleteItem[]) => void
      ) => {
        const trimmedValue = inputValue?.trim();

        if (!trimmedValue) {
          setOptions([]);
          callback([]);
          return;
        }

        setIsLoading(true);

        try {
          const apiEntityTypes = entityTypes?.filter(t => t !== "street_shortcut");
          const data = await searchEntityTypesByText(trimmedValue, apiEntityTypes);
          let results = data || [];

          if (entityTypes?.includes("street_shortcut")) {
            const streetShortcut: AutocompleteItem = { id: 0, name: trimmedValue, type: "street_shortcut" };
            results = [...results, streetShortcut];
          }

          setOptions(results);
          callback(results);
        } catch (error) {
          console.error('Error loading autocomplete options:', error);
          setOptions([]);
          callback([]);
        } finally {
          setIsLoading(false);
        }
      },
      300
    ),
    [entityTypes]
  );

  const formatOptionLabel = (option: AutocompleteItem) => {
    if (option.type === 'street' && option.municipality?.name) {
      return `${option.name}, ${option.municipality.name}`;
    }

    if (option.type === 'project' && option.city) {
      return `${option.name}, ${option.city}`;
    }

    return option.name;
  };

  return {
    isLoading,
    options,
    loadOptions,
    formatOptionLabel,
  };
};
